import { Component, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { CustomInputComponent } from '../../../shared-components/custom-input/custom-input.component';
import { CustomSelectComponent, SelectOption } from '../../../shared-components/custom-select/custom-select.component';
import { CustomCheckboxComponent } from '../../../shared-components/custom-checkbox/custom-checkbox.component';
import { CustomSwitchComponent } from '../../../shared-components/custom-switch/custom-switch.component';
import { CustomRadioComponent, RadioOption } from '../../../shared-components/custom-radio/custom-radio.component';
import { CustomDatePickerComponent } from '../../../shared-components/custom-date-picker/custom-date-picker.component';
import { CustomSliderComponent } from '../../../shared-components/custom-slider/custom-slider.component';
import { TranslateService } from '../../../services/translate/translate.service';
import { FormValidationUtils, ComponentType } from '../../../utils/form-validation';
import { SmartFormBaseComponent } from '../../../utils/smart-form-base.component';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';

@Component({
	selector: 'app-form-components-example',
	templateUrl: './form-components-example.component.html',
	styleUrls: ['./form-components-example.component.scss'],
	standalone: false,
})
export class FormComponentsExampleComponent extends SmartFormBaseComponent implements AfterViewInit {
	public testing: boolean = false;

	@ViewChildren(CustomInputComponent) inputComponents!: QueryList<CustomInputComponent>;
	@ViewChildren(CustomSelectComponent) selectComponents!: QueryList<CustomSelectComponent>;
	@ViewChildren(CustomCheckboxComponent) checkboxComponents!: QueryList<CustomCheckboxComponent>;
	@ViewChildren(CustomSwitchComponent) switchComponents!: QueryList<CustomSwitchComponent>;
	@ViewChildren(CustomRadioComponent) radioComponents!: QueryList<CustomRadioComponent>;
	@ViewChildren(CustomDatePickerComponent) datePickerComponents!: QueryList<CustomDatePickerComponent>;
	@ViewChildren(CustomSliderComponent) sliderComponents!: QueryList<CustomSliderComponent>;

	public categoryOptions: SelectOption[] = [
		{ label: 'Electronics', value: 'electronics' },
		{ label: 'Clothing', value: 'clothing' },
		{ label: 'Home & Garden', value: 'home-garden' },
		{ label: 'Sports', value: 'sports' },
		{ label: 'Books', value: 'books' },
	];

	public countryOptions: SelectOption[] = [
		{ label: 'United States', value: 'us' },
		{ label: 'United Kingdom', value: 'uk' },
		{ label: 'Germany', value: 'de' },
		{ label: 'France', value: 'fr' },
		{ label: 'Spain', value: 'es' },
	];

	public priorityOptions: RadioOption[] = [
		{ label: 'Low', value: 'low', displayValue: 'Low', icon: 'fa-regular fa-arrow-down' },
		{ label: 'Medium', value: 'medium', displayValue: 'Medium', icon: 'fa-solid fa-minus' },
		{ label: 'High', value: 'high', displayValue: 'High', icon: 'fa-regular fa-arrow-up' },
	];

	public shippingOptions: RadioOption[] = [
		{ label: 'Standard', value: 'standard', displayValue: 'Standard (5-7 days)', icon: 'fa-solid fa-truck' },
		{ label: 'Express', value: 'express', displayValue: 'Express (2-3 days)', icon: 'fa-solid fa-bolt' },
		{ label: 'Overnight', value: 'overnight', displayValue: 'Overnight', icon: 'fa-solid fa-rocket' },
	];

	// Phone validation - format: XXX-XXX-XXXX
	private get phoneValidationRules() {
		const inputRules = CustomInputComponent.createRules(this.translateService);
		return [inputRules.required(), inputRules.pattern(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be in format XXX-XXX-XXXX')];
	}

	// Username - required, min 3 chars, alphanumeric
	private get usernameValidationRules() {
		const inputRules = CustomInputComponent.createRules(this.translateService);
		return [
			inputRules.required(),
			inputRules.minLength(3, 'Username must be at least 3 characters'),
			inputRules.pattern(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
		];
	}

	// Password - required, min 8 chars
	private get passwordValidationRules() {
		const inputRules = CustomInputComponent.createRules(this.translateService);
		return [inputRules.required(), inputRules.minLength(8, 'Password must be at least 8 characters')];
	}

	// Quantity slider - required, must be >= 1
	private get quantitySliderValidationRules() {
		return [
			{
				validate: (value: any) => {
					const num = Number(value);
					return !isNaN(num) && num >= 1;
				},
				errorMessage: this.translateService.translate('field.required', 'client'),
				ruleId: 'required',
			},
		];
	}

	// Budget slider - custom: must be >= 100
	private get budgetSliderValidationRules() {
		return [
			{
				validate: (value: any) => {
					const num = Number(value);
					return !isNaN(num) && num >= 100;
				},
				errorMessage: 'Budget must be at least $100',
				ruleId: 'minBudget',
			},
		];
	}

	private get fieldConfigs() {
		return [
			{ name: 'fullName', type: ComponentType.INPUT },
			{ name: 'email', type: ComponentType.INPUT },
			{ name: 'phone', type: ComponentType.INPUT, validationRules: this.phoneValidationRules },
			{ name: 'username', type: ComponentType.INPUT, validationRules: this.usernameValidationRules },
			{ name: 'password', type: ComponentType.INPUT, validationRules: this.passwordValidationRules },
			{ name: 'category', type: ComponentType.SELECT },
			{ name: 'country', type: ComponentType.SELECT },
			{ name: 'priority', type: ComponentType.RADIO },
			{ name: 'shippingMethod', type: ComponentType.RADIO },
			{ name: 'quantity', type: ComponentType.SLIDER, validationRules: this.quantitySliderValidationRules },
			{ name: 'budget', type: ComponentType.SLIDER, validationRules: this.budgetSliderValidationRules },
			{ name: 'startDate', type: ComponentType.DATE_PICKER },
			{ name: 'endDate', type: ComponentType.DATE_PICKER },
			{ name: 'enableNotifications', type: ComponentType.SWITCH, validationRules: [] },
			{ name: 'autoRenewal', type: ComponentType.SWITCH, validationRules: [] },
			{ name: 'newsletter', type: ComponentType.CHECKBOX, validationRules: [] },
			{ name: 'acceptTerms', type: ComponentType.CHECKBOX },
		];
	}

	constructor(
		private translateService: TranslateService,
		private toasterService: ToasterService
	) {
		super();
	}

	ngAfterViewInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			this.fieldConfigs,
			this.translateService,
			this.inputComponents,
			this.selectComponents,
			this.checkboxComponents,
			this.switchComponents,
			this.radioComponents,
			this.datePickerComponents,
			this.sliderComponents
		);
	}

	override resetForm(): void {
		if (this.formManager) {
			this.formManager.reset();
			this.isFormValid = false;
		}
	}

	formatDate(value: any): string {
		if (!value) return '';
		if (Array.isArray(value) && value.length > 0 && value[0]) {
			const date = new Date(value[0]);
			return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
		}
		const date = new Date(value);
		return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
	}

	getFieldDisplayValue(fieldName: string): string {
		const field = this.formFields[fieldName];
		if (!field) return '-';
		const v = field.value;
		if (v === null || v === undefined || v === '') return 'No value';
		if (typeof v === 'boolean') return v ? 'Yes' : 'No';
		if (fieldName === 'startDate' || fieldName === 'endDate') return this.formatDate(v);
		if (fieldName === 'quantity') return `${v} units`;
		if (fieldName === 'budget') return `$${v}`;
		return String(v);
	}

	getFieldValidDisplay(fieldName: string): string {
		const field = this.formFields[fieldName];
		return field?.valid ? 'Yes' : 'No';
	}

	/** Cached to avoid new array on every CD (prevents cascading repaints on click) */
	private static readonly FORM_FIELD_ENTRIES: { key: string; label: string }[] = [
		{ key: 'fullName', label: 'Full Name' },
		{ key: 'email', label: 'Email' },
		{ key: 'phone', label: 'Phone' },
		{ key: 'username', label: 'Username' },
		{ key: 'password', label: 'Password' },
		{ key: 'category', label: 'Category' },
		{ key: 'country', label: 'Country' },
		{ key: 'priority', label: 'Priority' },
		{ key: 'shippingMethod', label: 'Shipping' },
		{ key: 'quantity', label: 'Quantity' },
		{ key: 'budget', label: 'Budget' },
		{ key: 'startDate', label: 'Start Date' },
		{ key: 'endDate', label: 'End Date' },
		{ key: 'enableNotifications', label: 'Notifications' },
		{ key: 'autoRenewal', label: 'Auto-renewal' },
		{ key: 'newsletter', label: 'Newsletter' },
		{ key: 'acceptTerms', label: 'Terms' },
	];

	getFormFieldEntries(): { key: string; label: string }[] {
		return FormComponentsExampleComponent.FORM_FIELD_ENTRIES;
	}

	testFormFunctionality(): void {
		if (this.formManager) {
			this.testing = true;
			this.formManager.setValues({
				fullName: 'Jane Smith',
				email: 'jane.smith@example.com',
				phone: '555-123-4567',
				username: 'janesmith',
				password: 'SecurePass123',
				category: 'electronics',
				country: 'us',
				priority: 'high',
				shippingMethod: 'express',
				quantity: 5,
				budget: 500,
				startDate: ['2025-03-01T00:00:00.000Z'],
				endDate: ['2025-03-15T00:00:00.000Z'],
				enableNotifications: true,
				autoRenewal: false,
				newsletter: true,
				acceptTerms: true,
			});
			this.formManager.validateAll();
			this.isFormValid = this.formManager.isValid();

			setTimeout(() => {
				this.testing = false;
				this.toasterService.showSuccess('Form tested successfully');
			}, 5000);
		}
	}
}
